/**
 * THERAPIST NOTES SEARCH SERVICE
 *
 * Búsqueda semántica RAG y búsqueda simple de apuntes.
 * Extraído de therapist-notes.service.ts para mantener responsabilidad única.
 */

import { db } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import { semanticLayerService } from '@/core/semantic';
import { searchEngine } from '@/core/infrastructure';
import { TherapistNote, TherapistNoteChunk } from '@/core/types/models/therapist/therapist-notes.model';
import { therapistNotesCrudService } from './therapist-notes-crud.service';

const logger = Logger.getLogger('TherapistNotesSearchService');

const COLLECTIONS = {
  CHUNKS: 'therapist_note_chunks',
};

class TherapistNotesSearchService {

  /**
   * Buscar chunks relevantes para una consulta (base del sistema RAG)
   * Usa SearchEngine para búsqueda híbrida: keyword + vector search
   */
  async searchChunks(therapistId: string, query: string, options?: {
    maxResults?: number;
    minRelevance?: number;
    noteIds?: string[];
    useVectorSearch?: boolean;
  }): Promise<Array<TherapistNoteChunk & { relevanceScore: number }>> {
    const maxResults = options?.maxResults || 10;
    const minRelevance = options?.minRelevance || 2;
    const useVector = options?.useVectorSearch !== false;

    try {
      const results = await (searchEngine as any).search(
        COLLECTIONS.CHUNKS,
        query,
        {
          maxResults: maxResults * 2,
          minScore: minRelevance,
          useSemanticExpansion: true,
          useVectorSearch: useVector,
          filters: {
            ownerId: therapistId,
            metadata: options?.noteIds ? { noteId: options.noteIds[0] } : undefined,
          },
        }
      );

      const mappedResults = results.map(r => ({
        ...r.document,
        relevanceScore: r.score,
      }));

      let filtered = mappedResults;
      if (options?.noteIds && options.noteIds.length > 0) {
        filtered = mappedResults.filter(chunk =>
          options.noteIds!.includes(chunk.noteId)
        );
      }

      logger.debug('Chunks searched with SearchEngine', {
        query, therapistId, results: filtered.length, usedVector: useVector,
      });

      return filtered.slice(0, maxResults);

    } catch (searchEngineError) {
      logger.warn('SearchEngine failed, using fallback search', {
        error: (searchEngineError as Error).message
      });
      return this.searchChunksFallback(therapistId, query, options);
    }
  }

  /**
   * Búsqueda de chunks manual (fallback cuando SearchEngine no está disponible)
   */
  private async searchChunksFallback(therapistId: string, query: string, options?: {
    maxResults?: number;
    minRelevance?: number;
    noteIds?: string[];
  }): Promise<Array<TherapistNoteChunk & { relevanceScore: number }>> {
    const maxResults = options?.maxResults || 10;
    const minRelevance = options?.minRelevance || 2;

    const expandedQuery = semanticLayerService.expandQuery(query);
    const keywords = expandedQuery.searchKeywords.slice(0, 10);

    let chunksQuery: any = db.collection(COLLECTIONS.CHUNKS)
      .where('therapistId', '==', therapistId);

    if (options?.noteIds && options.noteIds.length > 0) {
      chunksQuery = chunksQuery.where('noteId', 'in', options.noteIds.slice(0, 10));
    }

    const snapshot = await chunksQuery.limit(200).get();

    const scoredChunks = snapshot.docs.map((doc: any) => {
      const chunk = { id: doc.id, ...doc.data() } as TherapistNoteChunk;
      let score = 0;

      for (const keyword of keywords) {
        const keywordLower = keyword.toLowerCase();

        if (chunk.keywords.some((k: string) => k.toLowerCase().includes(keywordLower))) {
          score += 3;
        }
        if (chunk.content.toLowerCase().includes(keywordLower)) {
          score += 2;
        }
        if (chunk.semanticTags.some((t: string) => t.toLowerCase().includes(keywordLower))) {
          score += 2;
        }
      }

      for (const conceptId of expandedQuery.expandedConceptIds) {
        if (chunk.semanticTags.includes(conceptId)) {
          score += 5;
        }
      }

      return { ...chunk, relevanceScore: score };
    });

    return scoredChunks
      .filter((c: any) => c.relevanceScore >= minRelevance)
      .sort((a: any, b: any) => b.relevanceScore - a.relevanceScore)
      .slice(0, maxResults);
  }

  /**
   * Buscar apuntes por texto (búsqueda simple)
   */
  async searchNotes(therapistId: string, searchText: string, options?: {
    limit?: number;
  }): Promise<TherapistNote[]> {
    try {
      const limit = options?.limit || 20;
      const notes = await therapistNotesCrudService.getNotes(therapistId, { limit: 100 });

      const searchLower = searchText.toLowerCase();

      const matched = notes.filter(note => {
        if (note.title.toLowerCase().includes(searchLower)) return true;
        if (note.tags.some(t => t.toLowerCase().includes(searchLower))) return true;
        if (note.category?.toLowerCase().includes(searchLower)) return true;
        if (note.extractedText.toLowerCase().includes(searchLower)) return true;
        return false;
      });

      return matched.slice(0, limit);

    } catch (error: any) {
      logger.error('Failed to search notes', error, { therapistId });
      throw error;
    }
  }
}

export const therapistNotesSearchService = new TherapistNotesSearchService();
